/**
 * Chat Service
 * API client for document chat (RAG) functionality
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type {
  ChatMessage,
  ChatSession,
  ChatCitation,
  DocumentIndexStatus,
  SuggestedQuestion,
  ChatConfig,
  FeedbackRating,
  ChatStreamChunk,
} from "../types/chat.types";

// Session token storage key prefix
const SESSION_TOKEN_KEY = "chat_session_";

// =====================================================
// SESSION MANAGEMENT
// =====================================================

/**
 * Generate a unique session token for anonymous chat
 */
function generateSessionToken(): string {
  return `sess_${crypto.randomUUID()}`;
}

/**
 * Get or create a session token for a content item
 * Stores token in localStorage for session persistence
 */
export function getSessionToken(contentItemId: UUID): string {
  const storageKey = `${SESSION_TOKEN_KEY}${contentItemId}`;
  let token = localStorage.getItem(storageKey);

  if (!token) {
    token = generateSessionToken();
    localStorage.setItem(storageKey, token);
  }

  return token;
}

/**
 * Clear session token for a content item (start new conversation)
 */
export function clearSessionToken(contentItemId: UUID): void {
  const storageKey = `${SESSION_TOKEN_KEY}${contentItemId}`;
  localStorage.removeItem(storageKey);
}

/**
 * Get or create a chat session
 */
export async function getOrCreateSession(
  contentItemId: UUID,
  source: "viewer" | "embed" | "api" = "viewer",
  embedDomain?: string,
): Promise<ChatSession> {
  const sessionToken = getSessionToken(contentItemId);

  // Try to find existing session
  const { data: existingSession } = await supabase
    .from("chat_sessions")
    .select("*")
    .eq("session_token", sessionToken)
    .maybeSingle();

  if (existingSession) {
    return existingSession as ChatSession;
  }

  // Create new session
  const { data: newSession, error } = await supabase
    .from("chat_sessions")
    .insert({
      content_item_id: contentItemId,
      session_token: sessionToken,
      source,
      embed_domain: embedDomain,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating chat session:", error);
    throw error;
  }

  return newSession as ChatSession;
}

// =====================================================
// DOCUMENT INDEXING
// =====================================================

/**
 * Get the indexing status for a content item
 */
export async function getIndexStatus(
  contentItemId: UUID,
): Promise<DocumentIndexStatus | null> {
  const { data, error } = await supabase
    .from("document_index_status")
    .select("*")
    .eq("content_item_id", contentItemId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching index status:", error);
    throw error;
  }

  return data as DocumentIndexStatus | null;
}

/**
 * Trigger document indexing via edge function
 */
export async function triggerIndexing(contentItemId: UUID): Promise<void> {
  const { error } = await supabase.functions.invoke("index-document", {
    body: { contentItemId },
  });

  if (error) {
    console.error("Error triggering indexing:", error);
    throw error;
  }
}

/**
 * Check if a document is ready for chat
 */
export async function isDocumentReady(contentItemId: UUID): Promise<boolean> {
  const status = await getIndexStatus(contentItemId);
  return status?.status === "ready";
}

// =====================================================
// CHAT MESSAGES
// =====================================================

/**
 * Get chat messages for a session
 */
export async function getChatMessages(
  sessionToken: string,
): Promise<ChatMessage[]> {
  // First get the session
  const { data: session } = await supabase
    .from("chat_sessions")
    .select("id")
    .eq("session_token", sessionToken)
    .maybeSingle();

  if (!session) {
    return [];
  }

  const { data, error } = await supabase
    .from("chat_messages")
    .select("*")
    .eq("session_id", session.id)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching chat messages:", error);
    throw error;
  }

  return (data || []).map(normalizeMessage);
}

/**
 * Normalize message from database format to frontend format
 */
function normalizeMessage(msg: Record<string, unknown>): ChatMessage {
  return {
    id: msg.id as UUID,
    session_id: msg.session_id as UUID,
    role: msg.role as ChatMessage["role"],
    content: msg.content as string,
    citations: msg.citations as ChatCitation[] | undefined,
    chunks_retrieved: msg.chunks_retrieved as number | undefined,
    model_used: msg.model_used as string | undefined,
    tokens_prompt: msg.tokens_prompt as number | undefined,
    tokens_completion: msg.tokens_completion as number | undefined,
    response_time_ms: msg.response_time_ms as number | undefined,
    feedback_rating: msg.feedback_rating as FeedbackRating | null | undefined,
    feedback_comment: msg.feedback_comment as string | undefined,
    feedback_at: msg.feedback_at as string | undefined,
    created_at: msg.created_at as string,
  };
}

/**
 * Send a chat message with streaming support
 * Returns an async iterator for streaming responses
 */
export async function sendChatMessage(
  contentItemId: UUID,
  message: string,
  options?: {
    stream?: boolean;
    onChunk?: (chunk: ChatStreamChunk) => void;
    onComplete?: (message: ChatMessage) => void;
    onError?: (error: Error) => void;
  },
): Promise<ChatMessage | null> {
  const sessionToken = getSessionToken(contentItemId);
  const stream = options?.stream ?? true;

  try {
    // Get the Supabase URL and anon key
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Try to get the user's access token for authenticated requests
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const authToken = session?.access_token || supabaseAnonKey;

    const response = await fetch(`${supabaseUrl}/functions/v1/chat-rag`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        apikey: supabaseAnonKey,
      },
      body: JSON.stringify({
        contentItemId,
        sessionToken,
        message,
        stream,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Chat request failed: ${response.status}`,
      );
    }

    if (stream && response.body) {
      // Handle streaming response
      return await handleStreamingResponse(response.body, options);
    } else {
      // Handle non-streaming response
      const data = await response.json();
      if (options?.onComplete) {
        options.onComplete(data.message);
      }
      return data.message;
    }
  } catch (error) {
    console.error("Error sending chat message:", error);
    if (options?.onError && error instanceof Error) {
      options.onError(error);
    }
    throw error;
  }
}

/**
 * Handle streaming response from chat-rag edge function
 */
async function handleStreamingResponse(
  body: ReadableStream<Uint8Array>,
  options?: {
    onChunk?: (chunk: ChatStreamChunk) => void;
    onComplete?: (message: ChatMessage) => void;
    onError?: (error: Error) => void;
  },
): Promise<ChatMessage | null> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullContent = "";
  let citations: ChatCitation[] = [];
  let finalMessage: ChatMessage | null = null;

  try {
    let reading = true;
    while (reading) {
      const { done, value } = await reader.read();

      if (done) {
        reading = false;
        continue;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const jsonStr = line.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") {
            continue;
          }

          try {
            const chunk: ChatStreamChunk = JSON.parse(jsonStr);

            if (chunk.error) {
              throw new Error(chunk.error);
            }

            if (chunk.content) {
              fullContent += chunk.content;
            }

            if (chunk.citations) {
              citations = chunk.citations;
            }

            if (options?.onChunk) {
              options.onChunk({
                content: chunk.content,
                citations: chunk.citations,
                done: chunk.done,
              });
            }

            if (
              chunk.done &&
              (chunk as unknown as { message: ChatMessage }).message
            ) {
              finalMessage = (chunk as unknown as { message: ChatMessage })
                .message;
            }
          } catch (parseError) {
            // Skip malformed JSON
            console.warn("Failed to parse SSE chunk:", jsonStr);
          }
        }
      }
    }

    // If we didn't get a final message object, construct one
    if (!finalMessage && fullContent) {
      finalMessage = {
        id: crypto.randomUUID() as UUID,
        session_id: "" as UUID,
        role: "assistant",
        content: fullContent,
        citations,
        created_at: new Date().toISOString(),
      };
    }

    if (finalMessage && options?.onComplete) {
      options.onComplete(finalMessage);
    }

    return finalMessage;
  } catch (error) {
    console.error("Streaming error:", error);
    if (options?.onError && error instanceof Error) {
      options.onError(error);
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
}

// =====================================================
// FEEDBACK
// =====================================================

/**
 * Submit feedback for a chat message
 */
export async function submitFeedback(
  messageId: UUID,
  rating: FeedbackRating,
  comment?: string,
): Promise<void> {
  const { error } = await supabase
    .from("chat_messages")
    .update({
      feedback_rating: rating,
      feedback_comment: comment,
      feedback_at: new Date().toISOString(),
    })
    .eq("id", messageId);

  if (error) {
    console.error("Error submitting feedback:", error);
    throw error;
  }
}

/**
 * Clear feedback for a chat message
 */
export async function clearFeedback(messageId: UUID): Promise<void> {
  const { error } = await supabase
    .from("chat_messages")
    .update({
      feedback_rating: null,
      feedback_comment: null,
      feedback_at: null,
    })
    .eq("id", messageId);

  if (error) {
    console.error("Error clearing feedback:", error);
    throw error;
  }
}

// =====================================================
// SUGGESTED QUESTIONS
// =====================================================

/**
 * Get suggested questions for a content item
 */
export async function getSuggestedQuestions(
  contentItemId: UUID,
): Promise<SuggestedQuestion[]> {
  const { data, error } = await supabase
    .from("chat_suggested_questions")
    .select("*")
    .eq("content_item_id", contentItemId)
    .eq("is_active", true)
    .order("display_order", { ascending: true });

  if (error) {
    console.error("Error fetching suggested questions:", error);
    throw error;
  }

  return (data || []) as SuggestedQuestion[];
}

/**
 * Add a suggested question
 */
export async function addSuggestedQuestion(
  contentItemId: UUID,
  question: string,
  displayOrder?: number,
): Promise<SuggestedQuestion> {
  const { data, error } = await supabase
    .from("chat_suggested_questions")
    .insert({
      content_item_id: contentItemId,
      question,
      display_order: displayOrder ?? 0,
      source: "manual",
    })
    .select()
    .single();

  if (error) {
    console.error("Error adding suggested question:", error);
    throw error;
  }

  return data as SuggestedQuestion;
}

/**
 * Update a suggested question
 */
export async function updateSuggestedQuestion(
  id: UUID,
  updates: {
    question?: string;
    display_order?: number;
    is_active?: boolean;
  },
): Promise<void> {
  const { error } = await supabase
    .from("chat_suggested_questions")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    console.error("Error updating suggested question:", error);
    throw error;
  }
}

/**
 * Delete a suggested question
 */
export async function deleteSuggestedQuestion(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("chat_suggested_questions")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting suggested question:", error);
    throw error;
  }
}

/**
 * Reorder suggested questions
 */
export async function reorderSuggestedQuestions(
  contentItemId: UUID,
  questionIds: UUID[],
): Promise<void> {
  const { error } = await supabase.rpc("reorder_suggested_questions", {
    p_content_item_id: contentItemId,
    p_question_ids: questionIds,
  });

  if (error) {
    console.error("Error reordering suggested questions:", error);
    throw error;
  }
}

// =====================================================
// CHAT CONFIGURATION
// =====================================================

/**
 * Get chat configuration for a content item
 */
export async function getChatConfig(
  contentItemId: UUID,
): Promise<ChatConfig | null> {
  const { data, error } = await supabase
    .from("content_items")
    .select(
      "chat_enabled, chat_disclaimer, chat_system_prompt, show_top_questions",
    )
    .eq("id", contentItemId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching chat config:", error);
    throw error;
  }

  return data as ChatConfig;
}

/**
 * Update chat configuration for a content item
 */
export async function updateChatConfig(
  contentItemId: UUID,
  config: Partial<ChatConfig>,
): Promise<void> {
  const { error } = await supabase
    .from("content_items")
    .update({
      ...config,
      updated_at: new Date().toISOString(),
    })
    .eq("id", contentItemId);

  if (error) {
    console.error("Error updating chat config:", error);
    throw error;
  }
}

/**
 * Enable chat for a content item and trigger indexing if needed
 */
export async function enableChat(contentItemId: UUID): Promise<void> {
  // Update the chat_enabled flag
  await updateChatConfig(contentItemId, { chat_enabled: true });

  // Check if document is already indexed
  const status = await getIndexStatus(contentItemId);

  if (!status || status.status === "failed") {
    // Trigger indexing
    await triggerIndexing(contentItemId);
  }
}

/**
 * Disable chat for a content item
 */
export async function disableChat(contentItemId: UUID): Promise<void> {
  await updateChatConfig(contentItemId, { chat_enabled: false });
}
